import React, { useState, useEffect, useMemo, useRef } from "react";
import Nav from "./Nav";
import Footer from "./Footer";
import "./home.css";
import "./Sales.css";
import "../Components/ListpageExpress.css";
import * as XLSX from "xlsx";
import { useNavigate } from "react-router-dom";
import DataTable from "react-data-table-component";
import { Buffer } from "buffer";
import axios from "axios";
import { items } from "../HelperFx/datalinks";
import verifyToken from "../HelperFx/verifyJwt.js";
import {
  describeMatch,
  matchedWordsFor,
  searchItems,
} from "../HelperFx/itemSearch";
import { CircularProgress, Typography } from "@mui/material";
import Swal from "sweetalert2";

const REACT_UNAME = process.env.REACT_APP_API_USERNAME;
const REACT_PWD = process.env.REACT_APP_API_PASSWORD;
const REACT_DATA_URL = process.env.REACT_APP_API_DATA_URL;
const REACT_APP_COMPANY_URL = process.env.REACT_APP_COMPANY;

const PAGE_SIZE = 40;
const EXPORT_PAGE_SIZE = 200;
const LOAD_PAGE_SIZE = 1000;
const LIST_FIELDS = "no,description,itemCategory,baseUOM";
const token = Buffer.from(`${REACT_UNAME}:${REACT_PWD}`, "utf8").toString(
  "base64",
);

const buildItemsUrl = (top, skip, select) =>
  `${REACT_DATA_URL}${items}?${REACT_APP_COMPANY_URL}&$top=${top}&$skip=${skip}&$orderby=no${
    select ? `&$select=${select}` : ""
  }`;

const fetchItemsPage = async (top, skip, signal, select) => {
  const response = await axios.get(buildItemsUrl(top, skip, select), {
    headers: { Authorization: `Basic ${token}` },
    signal,
  });
  return response.data?.value || [];
};

const fetchAllListItems = async (signal) => {
  const all = [];
  for (let skip = 0; ; skip += LOAD_PAGE_SIZE) {
    const page = await fetchItemsPage(LOAD_PAGE_SIZE, skip, signal, LIST_FIELDS);
    all.push(...page);
    if (page.length < LOAD_PAGE_SIZE) break;
  }
  return all;
};

const compareValues = (a, b) => {
  if (typeof a === "number" && typeof b === "number") return a - b;
  return String(a ?? "").localeCompare(String(b ?? ""), undefined, {
    numeric: true,
    sensitivity: "base",
  });
};

const escapeRegExp = (text) => text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const Highlight = ({ text, words }) => {
  const value = String(text ?? "");
  const terms = [...new Set(words.filter(Boolean))].sort((a, b) => b.length - a.length);
  if (!value || !terms.length) return value;

  const pattern = new RegExp(`(${terms.map(escapeRegExp).join("|")})`, "gi");
  return value.split(pattern).map((part, index) =>
    index % 2 === 1 ? (
      <mark key={index} style={{ backgroundColor: "#ffe58f", padding: 0 }}>
        {part}
      </mark>
    ) : (
      <React.Fragment key={index}>{part}</React.Fragment>
    ),
  );
};

const highlighted = (row, key, label) => (
  <Highlight text={row[key]} words={matchedWordsFor(row.match, label)} />
);

const baseColumns = [
  {
    name: "No.",
    selector: (row) => row.no,
    cell: (row) => (
      <span style={{ cursor: "pointer" }}>{highlighted(row, "no", "No.")}</span>
    ),
    sortable: true,
  },
  {
    name: "Name/Description",
    selector: (row) => row.description,
    cell: (row) => highlighted(row, "description", "Description"),
    sortable: true,
    grow: 2,
    wrap: true,
  },
  {
    name: "Item Category",
    selector: (row) => row.itemCategory,
    cell: (row) => highlighted(row, "itemCategory", "Item Category"),
    sortable: true,
  },
  {
    name: "Units Of Measure",
    selector: (row) => row.baseUOM,
    cell: (row) => highlighted(row, "baseUOM", "Unit"),
    sortable: true,
  },
];

const matchColumn = {
  name: "Match",
  selector: (row) => row.match?.score || 0,
  cell: (row) => (
    <div style={{ lineHeight: 1.3, padding: "4px 0" }}>
      <div style={{ fontWeight: 600 }}>{Math.round((row.match?.score || 0) * 100)}%</div>
      <div style={{ fontSize: "0.75rem", color: "#555" }}>{describeMatch(row.match)}</div>
    </div>
  ),
  sortable: true,
  grow: 2,
  wrap: true,
};

const CustTable = ({ onExport }) => {
  const navigate = useNavigate();
  const jwttoken = sessionStorage.getItem("authToken");
  verifyToken(jwttoken);

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [allItems, setAllItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [reloadKey, setReloadKey] = useState(0);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [sort, setSort] = useState(null);
  const scrollRef = useRef(null);
  const sentinelRef = useRef(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 250);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError("");

    fetchAllListItems(controller.signal)
      .then((list) => setAllItems(list))
      .catch((err) => {
        if (err.code !== "ERR_CANCELED") {
          setError("Unable to load items.");
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, [reloadKey]);

  const searching = Boolean(debouncedSearch);
  const visibleRows = useMemo(
    () => (searching ? searchItems(allItems, debouncedSearch) : allItems),
    [allItems, debouncedSearch, searching],
  );
  const columns = useMemo(
    () => (searching ? [...baseColumns, matchColumn] : baseColumns),
    [searching],
  );

  const sortedRows = useMemo(() => {
    if (typeof sort?.selector !== "function") return visibleRows;
    const factor = sort.direction === "desc" ? -1 : 1;
    return [...visibleRows].sort(
      (a, b) => factor * compareValues(sort.selector(a), sort.selector(b)),
    );
  }, [visibleRows, sort]);

  const displayedRows = useMemo(
    () => sortedRows.slice(0, visibleCount),
    [sortedRows, visibleCount],
  );
  const hasMore = visibleCount < sortedRows.length;

  useEffect(() => {
    setSort(null);
  }, [debouncedSearch]);

  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [visibleRows, sort]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    const root = scrollRef.current;
    if (!sentinel || !root || !hasMore) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting) return;
        setVisibleCount((count) => Math.min(count + PAGE_SIZE, sortedRows.length));
      },
      { root, rootMargin: "200px", threshold: 0 },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, sortedRows.length, displayedRows.length]);

  return (
    <div className="express-list-page">
      <div className="express-list-toolbar">
        <p>Items</p>
        <button
          type="button"
          className="express-list-btn"
          onClick={() => navigate("/home")}
        >
          Back
        </button>
        <button type="button" className="express-list-btn" onClick={onExport}>
          Export To Excel
        </button>
      </div>

      <div className="express-list-search">
        <input
          type="text"
          placeholder="Search..."
          className="search-input"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />

        {error && (
          <Typography
            sx={{ color: "#c62828", fontSize: "0.85rem", flexBasis: "100%" }}
          >
            {error}{" "}
            <span
              role="button"
              style={{ textDecoration: "underline", cursor: "pointer" }}
              onClick={() => setReloadKey((key) => key + 1)}
            >
              Try again
            </span>
          </Typography>
        )}

        {!loading && !error && (
          <Typography sx={{ color: "#666", fontSize: "0.8rem", flexBasis: "100%" }}>
            {searching
              ? `${visibleRows.length} of ${allItems.length} items match (75% or better)`
              : `${allItems.length} items`}
          </Typography>
        )}
      </div>

      <div
        ref={scrollRef}
        className="express-list-scroller express-list-scroller-infinite"
      >
        <DataTable
          key={debouncedSearch}
          columns={columns}
          data={displayedRows}
          keyField="no"
          progressPending={loading}
          progressComponent={<CircularProgress size={28} sx={{ my: 3 }} />}
          persistTableHead
          highlightOnHover
          striped
          responsive
          sortServer
          onSort={(column, direction) =>
            setSort(
              typeof column?.selector === "function"
                ? { selector: column.selector, direction }
                : null,
            )
          }
          customStyles={{
            table: { style: { width: "100%" } },
            tableWrapper: { style: { width: "100%" } },
            headRow: {
              style: {
                position: "sticky",
                top: 0,
                zIndex: 2,
                backgroundColor: "#ffffff",
              },
            },
            rows: {
              style: {
                minHeight: "33px",
              },
            },
          }}
          noDataComponent={
            loading ? null : (
              <Typography sx={{ py: 3, color: "#666" }}>
                {searching ? "No items match 75% or better" : "No items found"}
              </Typography>
            )
          }
        />
        {!loading && sortedRows.length > 0 && (
          <div ref={sentinelRef} className="express-list-sentinel">
            {hasMore
              ? `Showing ${displayedRows.length} of ${sortedRows.length} — scroll for more`
              : "End of list"}
          </div>
        )}
      </div>
    </div>
  );
};

const Item = () => {
  const downloadItems = () => {
    Swal.fire({
      title: '<span style="font-size:1.3rem">Are you sure?</span>',
      text: "You want to export the items to Excel?",
      showCancelButton: true,
      confirmButtonText: "Yes, export",
      cancelButtonText: "Cancel",
    }).then(async (result) => {
      if (!result.isConfirmed) return;

      try {
        Swal.fire({
          title: "Exporting...",
          text: "Fetching items for Excel",
          allowOutsideClick: false,
          didOpen: () => Swal.showLoading(),
        });

        const exportData = [];
        let offset = 0;

        while (true) {
          const page = await fetchItemsPage(EXPORT_PAGE_SIZE, offset);
          exportData.push(
            ...page.map((item) => ({
              "No.": item.no,
              "Name/Description": item.description,
              "Item Category": item.itemCategory,
              "Units Of Measure": item.baseUOM,
            })),
          );

          if (page.length < EXPORT_PAGE_SIZE) break;
          offset += page.length;
        }

        const ws = XLSX.utils.json_to_sheet(exportData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Items");
        const date = new Date().toISOString().slice(0, 10);
        XLSX.writeFile(wb, `Items_Export_${date}.xlsx`);

        Swal.fire({
          title: `<span style="font-size:1.3rem">Exported Successful!</span>`,
          text: "Items data has been exported to Excel",
          timer: 2000,
          showConfirmButton: true,
        });
      } catch (_error) {
        Swal.fire({
          title: "Export Failed",
          text: "An error occurred while exporting items",
          icon: "error",
        });
      }
    });
  };

  return (
    <div className="Home">
      <Nav />
      <div className="dash-main settings-full">
        <CustTable onExport={downloadItems} />
      </div>
      <Footer />
    </div>
  );
};

export default Item;
